import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../styles/colors';
import { getMenuData } from '../data/menuData';
import { useMultiLanguage } from '../hooks/useMultiLanguage';
import MenuLanguageSwitcher from '../components/MenuLanguageSwitcher';

const AppetizerMenuContainer = styled.div`
  min-height: 100vh;
  background: ${colors.background.light};
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 3rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin-bottom: 1rem;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: ${colors.primary.green};
      border-radius: 2px;
    }
  }

  p {
    font-size: 1.2rem;
    color: ${colors.text.secondary};
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const MenuSection = styled(motion.section)`
  margin-bottom: 4rem;
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 2px solid ${colors.primary.green}20;

  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    color: ${colors.primary.green};
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.1rem;
    color: ${colors.text.secondary};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const MenuItem = styled(motion.div)`
  background: ${colors.background.light};
  border-radius: 15px;
  padding: 1.5rem;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: ${colors.primary.green};
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.primary.green}, ${colors.primary.blue});
    border-radius: 15px 15px 0 0;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  h3 {
    font-size: 1.4rem;
    font-weight: 600;
    color: ${colors.text.primary};
    margin: 0;
    flex: 1;
  }

  .price {
    font-size: 1.3rem;
    font-weight: 700;
    color: ${colors.primary.green};
    margin-left: 1rem;
  }
`;

const ItemNote = styled.p`
  color: ${colors.primary.blue};
  font-style: italic;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
`;

const MultiLanguageText = styled.div`
  .main-text {
    font-size: 0.95rem;
    color: ${colors.text.secondary};
    line-height: 1.6;
    margin-bottom: 0.3rem;
  }
  
  .secondary-text {
    font-size: 0.8rem;
    color: ${colors.text.secondary}80;
    line-height: 1.4;
    margin-bottom: 0.3rem;
  }
  
  .english-text {
    font-size: 0.7rem;
    color: ${colors.text.secondary}50;
    line-height: 1.3;
    font-style: italic;
    margin-bottom: 0.5rem;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${colors.primary.green};
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: ${colors.primary.blue};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '←';
    font-size: 1.2rem;
  }
`;

const BackButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 2rem;
`;

const AppetizerMenu = () => {
  const { t } = useTranslation();
  const { getTranslations } = useMultiLanguage();
  const petiscosData = getMenuData('petiscos');
  const bebidasData = getMenuData('bebidas');
  const drinksData = getMenuData('drinks');
  const cervejasData = getMenuData('cervejas');
  const sobremesasData = getMenuData('sobremesas');

  const renderMenuSection = (data, delay = 0) => {
    const sectionKey = data.title.toLowerCase().replace(/\s+/g, '');
    const translatedTitle = t(`menu.${sectionKey}`);
    const translatedSubtitle = t(`menu.${sectionKey}Subtitle`);
    
    return (
      <MenuSection
        key={data.title}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay }}
      >
        <SectionHeader>
          <h2>{translatedTitle}</h2>
          <p>{translatedSubtitle}</p>
          {data.title === "Aperitivi" && (
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: `${colors.primary.blue}15`, 
              borderRadius: '10px', 
              border: `2px solid ${colors.primary.blue}30`,
              display: 'inline-block'
            }}>
              <p style={{ 
                color: colors.primary.blue, 
                fontWeight: '600', 
                margin: '0', 
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🕔 {t('contact.aperitivoHours')}: {t('contact.aperitivoHoursText')}
              </p>
            </div>
          )}
        </SectionHeader>

      <MenuGrid>
        {data.items.map((item, index) => (
          <MenuItem
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: (delay * 0.1) + (index * 0.1) }}
            whileHover={{ scale: 1.02 }}
          >
            <ItemHeader>
              <h3>{getTranslations(item.id, 'name').mainText}</h3>
              <span className="price">{item.price}</span>
            </ItemHeader>
            <MultiLanguageText>
              <div className="main-text">{getTranslations(item.id, 'description').mainText}</div>
              {getTranslations(item.id, 'description').secondaryTexts.map((item, index) => (
                <div 
                  key={index} 
                  className={item.lang === 'en' ? 'english-text' : 'secondary-text'}
                >
                  {item.text}
                </div>
              ))}
            </MultiLanguageText>
            {item.unit && (
              <ItemNote>{t('menu.pricePer')} {item.unit}</ItemNote>
            )}
            {item.note && (
              <ItemNote>{item.note}</ItemNote>
            )}
          </MenuItem>
        ))}
      </MenuGrid>

      {data.note && (
        <div style={{ textAlign: 'center', marginTop: '2rem', padding: '1.5rem', background: `${colors.primary.green}10`, borderRadius: '10px', borderLeft: `4px solid ${colors.primary.green}` }}>
          <p style={{ color: colors.text.primary, fontWeight: '500', margin: '0', fontSize: '1rem' }}>{data.note}</p>
        </div>
      )}
    </MenuSection>
    );
  };

  return (
    <AppetizerMenuContainer>
      <Container>
        <BackButtonContainer>
          <BackButton to="/">
            {t('menu.backToSite', { defaultValue: 'Voltar ao Site' })}
          </BackButton>
        </BackButtonContainer>
        <MenuLanguageSwitcher />
        <Header>
          <h1>{t('menu.appetizerMenuTitle')}</h1>
          <p>{t('menu.appetizerMenuSubtitle')}</p>
        </Header>

        {renderMenuSection(petiscosData, 0)}
        {renderMenuSection(bebidasData, 0.2)}
        {renderMenuSection(drinksData, 0.4)}
        {renderMenuSection(cervejasData, 0.6)}
        {renderMenuSection(sobremesasData, 0.8)}
      </Container>
    </AppetizerMenuContainer>
  );
};

export default AppetizerMenu;
